/* eslint-disable id-blacklist */
import moment from 'moment';
import { defaultDateFormat } from '@adsk/bim360-matrix-react-components';
import T from 'i18n-react';

const formatStartDate = function(isoPureDateString, isUtc = false) {
  if (isoPureDateString) {
    const dateFunction = isUtc ? moment.utc : moment;
    return dateFunction(isoPureDateString).format();
  }
  return '';
};

const formatEndDate = function(isoPureDateString, isUtc = false) {
  if (isoPureDateString) {
    const dateFunction = isUtc ? moment.utc : moment;
    return dateFunction(isoPureDateString).add(1, 'days').format();
  }
  return '';
};

export const formatUtcDateRange = function(isoPureDateStringStart, isoPureDateStringEnd) {
  return `${formatStartDate(isoPureDateStringStart, true)}...${formatEndDate(isoPureDateStringEnd, true)}`;
};

export const formatLocalDateRange = function(isoPureDateStringStart, isoPureDateStringEnd) {
  return `${formatStartDate(isoPureDateStringStart)}...${formatEndDate(isoPureDateStringEnd)}`;
};

export const formatDateRange = function(startDate, endDate) {
  return `${moment(startDate).format('YYYY-MM-DDT00:00:00')}Z,${moment(endDate).format('YYYY-MM-DDT23:59:59')}Z`;
};

// For formatting into an ISO-8601 date string, sans time:
const ISO_DATE_FORMAT = 'YYYY-MM-DD';
export const toIsoDate = dateObjectOrString => dateObjectOrString ? moment(dateObjectOrString).format(ISO_DATE_FORMAT) : null;

// For formatting into an ISO-8601 date string, sans time:
const ISO_DATE_FORMAT_WITH_TIME = 'YYYY-MM-DD HH:mm:SS';
export const toIsoDateWithTime = dateObjectOrString => dateObjectOrString ? moment(dateObjectOrString).format(ISO_DATE_FORMAT_WITH_TIME) : null;

// For converting ISO-8601 date string into a native JavaScript Date Object:
// (Returns null if utcIsoDateString is falsey)
// toLocalDate accepts: 'YYYY-MM-DD' || 'YYYY-MM-DD<time><tz>'
export const toLocalDate = utcIsoDateString => {
  if (!utcIsoDateString) {
    return null;
  }
  // If utcIsoDateString has a time (e.g.: '2016-06-16T00:00:00.000Z'),
  // it must be converted to a pure-date string, *without* time (e.g.: '2016-06-16'):
  const pureDate = toIsoDate(moment.utc(utcIsoDateString));
  return moment(pureDate).toDate();
};

export const i18nShortDateFormat = () => moment.localeData().longDateFormat('L');
export const i18nShortDateAndTimeFormat = () => i18nShortDateFormat() + ' hh:mm A';
// en: 'MM/DD/YYYY'
// de: 'DD.MM.YYYY'
export const toUtcShortDate = utcIsoDateTimeString => moment.utc(utcIsoDateTimeString).format(i18nShortDateFormat());
export const toShortDateAndTime = utcIsoDateTimeString => moment(utcIsoDateTimeString).format(i18nShortDateAndTimeFormat());
export const toLocalShortDate = utcIsoDateTimeString => moment(utcIsoDateTimeString).format(i18nShortDateFormat());

export const i18nLongDateFormat = () => moment.localeData().longDateFormat('ll');
// en: 'MMM D, YYYY'
// de: 'D. MMM YYYY'
export const toUtcLongDate = utcIsoDateTimeString => moment.utc(utcIsoDateTimeString).format(i18nLongDateFormat());
export const toLocalLongDate = utcIsoDateTimeString => moment(utcIsoDateTimeString).format(i18nLongDateFormat());

export const i18nDateTimeFormat = () => moment.localeData().longDateFormat('lll');
// en: 'MMM D, YYYY h:mm A'
// de: 'D. MMM YYYY HH:mm'
// Displays as local browser time -- not UTC:
export const toLocalDateTime = utcIsoDateTimeString => moment(utcIsoDateTimeString).format(i18nDateTimeFormat());

// This returns a native JavaScript Date object:
export const now = () => moment().toDate();

// Checks if given date was already past:
export const isHistory = (utcIsoDateTimeString) => moment.utc(utcIsoDateTimeString).isBefore(moment());

// Check if a dueDate is late (less than 1 day ahead)
export const isLate = (dueDate) => {
  if (!dueDate) {
    return false;
  }

  const wrappedDueDate = moment(dueDate);
  const wrappedTomorrow = moment().add(1, 'days');
  const diffInMS = moment.duration(wrappedDueDate.diff(wrappedTomorrow));

  return diffInMS < 0;
};

const REPORT_DATE_FORMAT = 'MM-DD-YY';
export const toReportFileNameDate = dateObjectOrString => dateObjectOrString ? moment(dateObjectOrString).format(REPORT_DATE_FORMAT) : null;

export const diffInMS = (start, end) => {
  const wrappedStartDate = moment(start);
  const wrappedEndDate = moment(end);
  return moment.duration(wrappedEndDate.diff(wrappedStartDate));
};

export const shortRelativeDateString = (date) => {
  const momentDate = moment(date).startOf('day');
  const today = moment().startOf('day');
  const dayDiff = momentDate.diff(today, 'days');

  switch (dayDiff) {
    case 1:
      return T.translate('date.tomorrow');
    case 0:
      return T.translate('date.today');
    case -1:
      return T.translate('date.yesterday');
    default:
      return momentDate.format(defaultDateFormat(momentDate));
  }
};

export const toNativeIsoDate = date => {
  return date ? moment(date).toISOString() : undefined;
};

/* eslint-enable id-blacklist */
